import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import {
    ChevronRight,
    ChevronLeft,
    Mail,
    Phone,
    Building2,
    Calendar,
    ArrowUpDown,
    FileText,
    Users as UsersGroupIcon,
    BarChart3,
    AlertTriangle,
    MessageSquare,
    Inbox,
    Pencil,
} from "lucide-react";

import { stripHtml } from "../../../../../Utility/sanitizeHtml";
import { GetCompanyAssessmentByUID } from "../../../Services/SurveyService";


// ---------------------------------------------------------
// STATUS
// ---------------------------------------------------------

const STATUS_STYLES = {
    "Not Started": "bg-blue-50 text-blue-500",
    "In Progress": "bg-orange-50 text-orange-500",
    "Completed": "bg-[#c2a8f026] text-[#7f35b2]",
    "Overdue": "bg-red-50 text-red-500",
};


// ---------------------------------------------------------
// ASSESSMENT ICONS
// ---------------------------------------------------------

const ASSESSMENT_ICONS = [
    {
        icon: FileText,
        bg: "bg-[#c2a8f026]",
        color: "text-[#7f35b2]",
    },
    {
        icon: UsersGroupIcon,
        bg: "bg-purple-50",
        color: "text-purple-500",
    },
    {
        icon: BarChart3,
        bg: "bg-amber-50",
        color: "text-amber-500",
    },
    {
        icon: AlertTriangle,
        bg: "bg-orange-50",
        color: "text-orange-500",
    },
    {
        icon: MessageSquare,
        bg: "bg-blue-50",
        color: "text-blue-500",
    },
];


// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------

const initials = (name) =>
    name
        ? name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()
        : "";


const getStatus = (startDateTime, endDateTime) => {
    if (!startDateTime) return "Not Started";

    const now = new Date();
    const start = new Date(startDateTime);
    const end = endDateTime ? new Date(endDateTime) : null;

    if (now < start) return "Not Started";
    if (end && now > end) return "Overdue";

    return "In Progress";
};


// ---------------------------------------------------------
// INFO ITEM
// ---------------------------------------------------------

const InfoItem = ({
    icon: Icon,
    label,
    value,
    align = "left",
}) => (
    <div className={align === "right" ? "text-right" : ""}>
        <div
            className={`flex items-center gap-1.5 text-xs font-medium text-gray-400 ${
                align === "right" ? "justify-end" : ""
            }`}
        >
            <Icon size={13} className="text-[#7f35b2]" />
            {label}
        </div>

        <p className="mt-1 text-sm font-medium text-gray-900">
            {value || "—"}
        </p>
    </div>
);


// ---------------------------------------------------------
// SORTABLE HEADER
// ---------------------------------------------------------

const SortableHeader = ({ label }) => (
    <th className="py-3 pr-3">
        <span className="flex items-center gap-1">
            {label}
            <ArrowUpDown
                size={11}
                className="text-gray-300"
            />
        </span>
    </th>
);


// ---------------------------------------------------------
// COMPANY CARD SKELETON
// ---------------------------------------------------------

const CompanyCardSkeleton = () => (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-6 rounded-2xl border border-gray-100 bg-white p-6 shadow-md animate-pulse">

        <div className="flex items-center gap-4">
            <div className="h-16 w-16 shrink-0 rounded-xl bg-gray-200" />

            <div className="space-y-2">
                <div className="h-4 w-40 rounded bg-gray-200" />
                <div className="h-3 w-28 rounded bg-gray-200" />
                <div className="h-5 w-16 rounded-full bg-gray-200" />
            </div>
        </div>

        <div className="flex flex-1 flex-wrap gap-8">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-2">
                    <div className="h-3 w-24 rounded bg-gray-200" />
                    <div className="h-4 w-32 rounded bg-gray-200" />
                </div>
            ))}
        </div>

        <div className="h-9 w-28 rounded-lg bg-gray-200" />
    </div>
);


// ---------------------------------------------------------
// ASSESSMENT ROW SKELETON
// ---------------------------------------------------------

const AssessmentRowSkeleton = () => (
    <tr className="border-b border-gray-50 animate-pulse">

        <td className="py-3.5 pr-3">
            <div className="flex items-center gap-2.5">
                <div className="h-8 w-8 shrink-0 rounded-lg bg-gray-200" />
                <div className="h-4 w-40 rounded bg-gray-200" />
            </div>
        </td>

        {Array.from({ length: 6 }).map((_, i) => (
            <td
                key={i}
                className="py-3.5 pr-3"
            >
                <div className="h-4 w-16 rounded bg-gray-200" />
            </td>
        ))}

        <td className="py-3.5 pr-3">
            <div className="h-7 w-20 rounded-md bg-gray-200" />
        </td>
    </tr>
);


// ---------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------

const CompanyAssessment = () => {

    const location = useLocation();
    const navigate = useNavigate();

    // Company should come from the previous page.
    const [company, setCompany] = useState(
        location.state?.company || null
    );

    const [assessments, setAssessments] = useState([]);

    const [assessmentsLoading, setAssessmentsLoading] =
        useState(false);
    const [assessmentsError, setAssessmentsError] =
        useState(null);

    // -----------------------------------------------------
    // FETCH COMPANY ASSESSMENTS
    // -----------------------------------------------------

    useEffect(() => {

        // Change this according to your actual company UID.
        const companyUID =
            company?.companyUID ||
            company?.companyUid ||
            company?.CompanyUID ||
            company?.id;

        if (!companyUID) return;

        const fetchAssessments = async () => {

            setAssessmentsLoading(true);
            setAssessmentsError(null);

            try {

                const res =
                    await GetCompanyAssessmentByUID(
                        companyUID
                    );


                const list =
                    res?.data?.assessmentList || [];


                const mapped = list.map((item) => ({

                    assessmentId:
                        item.assessmentUID,

                    assessmentName:
                        item.assessmentName,

                    assessmentDesc:
                        stripHtml(item.description),

                    totalQuestions:
                        item.totalQuestions ?? 0,

                    totalResponse:
                        item.totalSubmittedResponses ?? 0,

                    duration:
                        `${item.sectionADurationMinutes || 0}m / ${
                            item.sectionBDurationMinutes || 0
                        }m`,

                    assignedOn:
                        item.assignedDate,

                    startDate:
                        item.startDateTime,

                    dueDate:
                        item.endDateTime,

                    status:
                        getStatus(
                            item.startDateTime,
                            item.endDateTime
                        ),
                }));


                setAssessments(mapped);

            } catch (err) {

                setAssessmentsError(
                    err?.response?.data?.message ||
                    "Failed to load assigned assessments."
                );

                setAssessments([]);

            } finally {

                setAssessmentsLoading(false);
            }
        };


        fetchAssessments();

    }, [company]);

    // -----------------------------------------------------
    // UI
    // -----------------------------------------------------

    return (
        <motion.div
            initial={{
                opacity: 0,
                y: 12,
            }}
            animate={{
                opacity: 1,
                y: 0,
            }}
            transition={{
                duration: 0.3,
            }}
            className="mx-auto max-w-full"
        >

            {/* ============================================
                PAGE HEADER
            ============================================ */}

            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">

                <div>

                    <h1 className="text-2xl font-bold text-gray-900">
                        Company Assessment
                    </h1>

                    <div className="mt-1 flex items-center gap-1.5 text-sm">

                        <span className="text-gray-500">
                            Assessments
                        </span>

                        <ChevronRight
                            size={14}
                            className="text-gray-400"
                        />

                        <span className="font-medium text-[#7f35b2]">
                            Company Assessment
                        </span>

                    </div>

                    <p className="mt-1 text-sm text-gray-400">
                        View company details and their assigned assessments.
                    </p>

                </div>

            </div>


            {/* ============================================
                COMPANY INFORMATION CARD
            ============================================ */}

            {!company ? (

                <CompanyCardSkeleton />

            ) : (

                <div className="mb-6 flex flex-wrap items-start justify-between gap-6 rounded-2xl border border-gray-100 bg-white p-6 shadow-md">

                    {/* COMPANY */}
                    <div className="flex items-center gap-4">

                        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-[#c2a8f033] text-xl font-semibold text-[#7f35b2]">

                            {company.logo ? (
                                <img
                                    src={company.logo}
                                    alt={company.companyName}
                                    className="h-full w-full rounded-xl object-contain"
                                />
                            ) : (
                                initials(
                                    company.companyName ||
                                    company.name
                                )
                            )}

                        </div>


                        <div>

                            <h2 className="text-lg font-bold text-gray-900">
                                {company.companyName ||
                                    company.name ||
                                    "Company"}
                            </h2>

                            <p className="text-sm text-gray-500">
                                {company.companyType ||
                                    company.type ||
                                    "Company"}
                            </p>

                            <span
                                className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                                    company.isActive
                                        ? "bg-[#c2a8f026] text-[#7f35b2]"
                                        : "bg-[#c2a8f026] text-[#7f35b2]"
                                }`}
                            >
                                {/* <span
                                    className={`h-1.5 w-1.5 rounded-full ${
                                        company.isActive
                                            ? "bg-[#7f35b2]"
                                            : "bg-[#c2a8f026]"
                                    }`}
                                /> */}

                                 {(company.isActive ?? company.status)
                                    ? "Active"
                                    : "InActive"}
                            </span>

                        </div>

                    </div>


                    {/* COMPANY DETAILS */}

                    <div className="flex flex-1 flex-wrap items-center justify-evenly gap-x-5 gap-y-4">

                        <InfoItem
                            icon={Mail}
                            label="Email"
                            value={
                                company.email ||
                                company.contactMail
                            }
                        />

                        <InfoItem
                            icon={Phone}
                            label="Contact Number"
                            value={
                                company.mobileNumber ||
                                company.contactNumber ||
                                company.phone
                            }
                        />

                        <InfoItem
                            icon={Calendar}
                            label="Date Created"
                            value={
                                company.dateCreated ||
                                company.createdOn ||
                                company.establishmentDate
                            }
                            align="right"
                        />

                    </div>


                </div>
            )}


            {/* ============================================
                ASSIGNED ASSESSMENTS
            ============================================ */}

            <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-md">

                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">

                    <div className="flex items-center gap-3">

                        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#c2a8f033]">

                            <FileText
                                size={18}
                                className="text-[#7f35b2]"
                            />

                        </div>

                        <div>

                            <h3 className="text-sm font-bold text-gray-900">
                                Assigned Assessments
                            </h3>

                            <p className="text-xs text-gray-400">
                                Assessments assigned to this company.
                            </p>

                        </div>

                    </div>

                </div>


                {/* TABLE */}

                <div className="overflow-x-auto">

                    <table className="w-full text-left text-sm">

                        <thead>

                            <tr className="border-b border-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-400">

                                <SortableHeader label="Assessment Name" />

                                <SortableHeader label="Total Questions" />

                                <th className="py-3 pr-3">
                                    Total Responses
                                </th>

                                <SortableHeader label="Duration" />

                                <SortableHeader label="Assigned On" />

                                <SortableHeader label="Start Date" />

                                <SortableHeader label="Due Date" />

                                <SortableHeader label="Status" />

                            </tr>

                        </thead>


                        <tbody>

                            {assessmentsLoading ? (

                                Array.from({
                                    length: 5,
                                }).map((_, idx) => (
                                    <AssessmentRowSkeleton
                                        key={idx}
                                    />
                                ))

                            ) : assessmentsError ? (

                                <tr>

                                    <td
                                        colSpan="8"
                                        className="py-12 text-center text-red-500"
                                    >

                                        <div className="flex flex-col items-center justify-center gap-2">

                                            <AlertTriangle
                                                size={32}
                                                className="text-red-300"
                                            />

                                            <p className="text-sm font-medium">
                                                {assessmentsError}
                                            </p>

                                        </div>

                                    </td>

                                </tr>

                            ) : assessments.length === 0 ? (

                                <tr>

                                    <td
                                        colSpan="8"
                                        className="py-12 text-center text-gray-400"
                                    >

                                        <div className="flex flex-col items-center justify-center gap-2">

                                            <Inbox
                                                size={32}
                                                className="text-gray-300"
                                            />

                                            <p className="text-sm font-medium">
                                                No assessments assigned
                                            </p>

                                        </div>

                                    </td>

                                </tr>

                            ) : (

                                assessments.map(
                                    (item, idx) => {

                                        const {
                                            icon: Icon,
                                            bg,
                                            color,
                                        } =
                                            ASSESSMENT_ICONS[
                                                idx %
                                                    ASSESSMENT_ICONS.length
                                            ];


                                        return (
                                            <tr
                                                key={
                                                    item.assessmentId ||
                                                    idx
                                                }
                                                className="border-b border-gray-50 last:border-0"
                                            >

                                                {/* ASSESSMENT */}

                                                <td className="py-3.5 pr-3">

                                                    <div className="flex items-center gap-2.5">

                                                        <div
                                                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${bg}`}
                                                        >

                                                            <Icon
                                                                size={15}
                                                                className={color}
                                                            />

                                                        </div>


                                                        <div className="min-w-0">

                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/dashboard/Assessment-List/Add-Questions/assign-users/${item.assessmentId}`,
                                                                        {
                                                                            state: {
                                                                                surveyUID:
                                                                                    item.assessmentId,

                                                                                surveyName:
                                                                                    item.assessmentName,

                                                                                surveyDescription:
                                                                                    item.assessmentDesc,

                                                                                surveyStatus:
                                                                                    "active",
                                                                            },
                                                                        }
                                                                    )
                                                                }
                                                                className="block text-left font-medium text-gray-900 hover:text-[#7f35b2] hover:underline"
                                                            >
                                                                {
                                                                    item.assessmentName
                                                                }
                                                            </button>


                                                            {item.assessmentDesc && (
                                                                <p
                                                                    className="max-w-[220px] truncate text-xs text-gray-400"
                                                                    title={
                                                                        item.assessmentDesc
                                                                    }
                                                                >
                                                                    {
                                                                        item.assessmentDesc
                                                                    }
                                                                </p>
                                                            )}

                                                        </div>

                                                    </div>

                                                </td>


                                                <td className="py-3.5 pr-3 text-gray-500">
                                                    {
                                                        item.totalQuestions
                                                    }
                                                </td>


                                                <td className="py-3.5 pr-3 text-gray-500">
                                                    {
                                                        item.totalResponse
                                                    }
                                                </td>


                                                <td className="py-3.5 pr-3 text-gray-500">
                                                    {
                                                        item.duration
                                                    }
                                                </td>


                                                <td className="py-3.5 pr-3 text-gray-500">
                                                    {
                                                        item.assignedOn
                                                    }
                                                </td>


                                                <td className="py-3.5 pr-3 text-gray-500">
                                                    {
                                                        item.startDate
                                                    }
                                                </td>


                                                <td className="py-3.5 pr-3 text-gray-500">
                                                    {
                                                        item.dueDate
                                                    }
                                                </td>


                                                <td className="py-3.5 pr-3">

                                                    <span
                                                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                                                            STATUS_STYLES[
                                                                item.status
                                                            ] ||
                                                            "bg-gray-100 text-gray-600"
                                                        }`}
                                                    >
                                                        {
                                                            item.status
                                                        }
                                                    </span>

                                                </td>

                                            </tr>
                                        );
                                    }
                                )

                            )}

                        </tbody>

                    </table>

                </div>


                {/* ========================================
                    PAGINATION
                ======================================== */}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm">

                    <p className="text-gray-500">
                        Showing{" "}
                        {assessments.length > 0
                            ? 1
                            : 0}{" "}
                        to{" "}
                        {assessments.length}{" "}
                        of{" "}
                        {assessments.length}{" "}
                        entries
                    </p>


                    <div className="flex items-center gap-1.5">

                        <button
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50"
                        >
                            <ChevronLeft size={15} />
                        </button>


                        <button
                            className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#7f35b2] text-sm font-medium text-white"
                        >
                            1
                        </button>


                        <button
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-50"
                        >
                            <ChevronRight size={15} />
                        </button>

                    </div>

                </div>

            </div>

        </motion.div>
    );
};


export default CompanyAssessment;